import { Reveal, StaggerGroup, StaggerItem } from "@/components/Reveal";
import { Tick } from "@/components/Annotation";
import { useLang } from "@/i18n";

/**
 * Section 4 — "What you prepare". Benefits/content must stay visible
 * without opening an accordion on mobile (per mobile rules), so unlike
 * the Method section, there is no collapse/expand here — just a compact
 * vertical list on mobile and a 2x2 grid on desktop.
 */
export const Outcomes = () => {
  const { t } = useLang();
  const cards = t("landing.prepare_cards");
  const cardsMobile = t("landing.prepare_cards_mobile");
  return (
    <section id="outcomes" className="relative z-10 py-24 md:py-36" data-testid="outcomes-section">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="grid lg:grid-cols-12 gap-8 mb-16 md:mb-24 items-end">
          <Reveal className="lg:col-span-7">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">
              {t("landing.prepare_eyebrow")}
            </p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-ink">
              {t("landing.prepare_h")}
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-5">
            <p className="hidden md:block font-body text-base md:text-lg text-ink-soft leading-relaxed" data-testid="prepare-body-desktop">
              {t("landing.prepare_body")}
            </p>
            <p className="md:hidden font-body text-ink-soft leading-relaxed" data-testid="prepare-body-mobile">
              {t("landing.prepare_body_mobile")}
            </p>
          </Reveal>
        </div>

        {/* Desktop: 2x2 grid with full body copy */}
        <StaggerGroup className="hidden md:grid md:grid-cols-2 gap-px bg-ink/10 border border-ink/10">
          {Array.isArray(cards) &&
            cards.map((o, i) => (
              <StaggerItem key={i} className="group bg-paper p-8 md:p-10 transition-colors duration-500 hover:bg-paper-deep/50">
                <div className="flex items-start justify-between mb-6">
                  <Tick className="w-7 h-7" />
                  <span className="font-hand text-2xl text-pencil group-hover:text-terracotta transition-colors duration-500">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <h3 className="font-serif text-2xl md:text-3xl text-ink mb-3 tracking-tight">{o.title}</h3>
                <p className="font-body text-ink-soft leading-relaxed">{o.body}</p>
              </StaggerItem>
            ))}
        </StaggerGroup>

        {/* Mobile: compact vertical list — fully visible, no accordion */}
        <StaggerGroup className="md:hidden flex flex-col gap-4">
          {Array.isArray(cardsMobile) &&
            cardsMobile.map((o, i) => (
              <StaggerItem key={i} className="bg-paper border border-ink/10 p-6" data-testid={`prepare-card-${i}`}>
                <div className="flex items-start justify-between mb-3">
                  <Tick className="w-6 h-6" />
                  <span className="font-hand text-xl text-pencil">{String(i + 1).padStart(2, "0")}</span>
                </div>
                <h3 className="font-serif text-xl text-ink mb-2 tracking-tight">{o.title}</h3>
                <p className="font-body text-sm text-ink-soft leading-relaxed">{o.body}</p>
              </StaggerItem>
            ))}
        </StaggerGroup>
      </div>
    </section>
  );
};
